import { Layout } from "@/components/layout/layout";
import { useLocation } from "wouter";
import { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import {
  useLookupPlayerPasswordReset,
  useAcceptPlayerPasswordReset,
  getGetPlayerMeQueryKey,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";

type LookupState =
  | { status: "loading" }
  | { status: "ok"; name: string; email: string }
  | { status: "error"; message: string };

export default function SetPassword() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const qc = useQueryClient();

  const [token, setToken] = useState<string | null>(null);
  const [lookupState, setLookupState] = useState<LookupState>({ status: "loading" });
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");

  const lookup = useLookupPlayerPasswordReset();
  const accept = useAcceptPlayerPasswordReset();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get("token");
    if (!t) {
      setLookupState({ status: "error", message: "Missing token in link." });
      return;
    }
    setToken(t);
    lookup.mutate(
      { data: { token: t } },
      {
        onSuccess: (info) => {
          setLookupState({ status: "ok", name: info.name, email: info.email });
        },
        onError: () => {
          setLookupState({
            status: "error",
            message: "This setup link is invalid or has expired.",
          });
        },
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    if (password.length < 8) {
      toast({
        title: "Password too short",
        description: "Use at least 8 characters.",
        variant: "destructive",
      });
      return;
    }
    if (password !== confirm) {
      toast({
        title: "Passwords don't match",
        description: "Re-enter the same password in both fields.",
        variant: "destructive",
      });
      return;
    }

    accept.mutate(
      { data: { token, password } },
      {
        onSuccess: (player) => {
          qc.setQueryData(getGetPlayerMeQueryKey(), player);
          toast({
            title: "Password set",
            description: `Welcome back, ${player.name}.`,
          });
          setLocation("/me");
        },
        onError: () => {
          toast({
            title: "Couldn't set password",
            description: "The link may have expired. Request a new one from the sign-in page.",
            variant: "destructive",
          });
        },
      },
    );
  };

  return (
    <Layout>
      <div className="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 bg-background relative">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-secondary/10 via-background to-background" />
        <Card className="w-full max-w-md relative z-10 border-border/50 shadow-2xl shadow-primary/5">
          <CardHeader className="space-y-1 text-center pb-6 pt-8">
            <CardTitle className="text-3xl font-bold tracking-tight">Set your password</CardTitle>
            <CardDescription className="text-base text-muted-foreground pt-2">
              {lookupState.status === "ok"
                ? `Choose a password for ${lookupState.email}.`
                : "Verifying your link..."}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {lookupState.status === "loading" && (
              <div className="flex justify-center py-10">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            )}
            {lookupState.status === "error" && (
              <div className="text-center py-6 space-y-4">
                <p className="text-destructive font-medium">{lookupState.message}</p>
                <Button onClick={() => setLocation("/login")}>Back to sign in</Button>
              </div>
            )}
            {lookupState.status === "ok" && (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-foreground font-medium">
                    New password
                  </Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="At least 8 characters"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={8}
                    autoComplete="new-password"
                    className="h-12 bg-muted/50 focus:bg-background"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm" className="text-foreground font-medium">
                    Confirm password
                  </Label>
                  <Input
                    id="confirm"
                    type="password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                    minLength={8}
                    autoComplete="new-password"
                    className="h-12 bg-muted/50 focus:bg-background"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full h-12 text-base font-semibold"
                  disabled={accept.isPending || !password || !confirm}
                >
                  {accept.isPending ? (
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  ) : null}
                  {accept.isPending ? "Saving..." : "Set password & sign in"}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
